import { Banner, Button } from '../design/components'
import type { Notice } from '../lib/envelope'
import styles from './NoticeList.module.css'

export interface NoticeListProps {
  notices: readonly Notice[]
  /** A failed mutation's message. Floated as a toast, assertively. */
  failure?: string | null
  onDismiss?: () => void
}

/**
 * The one notice renderer.
 *
 * Every domain's mutation envelope carries the same `{ level, code, message }`
 * shape, so this is written once and reused by characters, campaigns, scenes and
 * the rest. The `code` is the stable, machine-readable half — it is the React key
 * here, and the hook a future translation layer would use; `message` is only the
 * server's default English.
 *
 * The two halves are placed differently on purpose. **Notices** describe what a
 * change did and are read at leisure, so they sit inline where the page put them.
 * A **failure** is the opposite: it is the answer to something the reader just
 * did, and on a long sheet the form they did it in is usually nowhere near the
 * top. Rendered inline it scrolled off-screen and the edit looked like it had
 * silently done nothing — so it floats, fixed to the viewport, until it is
 * dismissed or the next successful change clears it.
 */
export function NoticeList({ notices, failure, onDismiss }: NoticeListProps) {
  const hasFailure = failure !== null && failure !== undefined
  if (notices.length === 0 && !hasFailure) return null

  return (
    <>
      {hasFailure && (
        <div className={styles.toast}>
          <Banner
            tone="error"
            urgent
            actions={
              onDismiss !== undefined ? (
                <Button variant="secondary" onClick={onDismiss}>
                  Dismiss
                </Button>
              ) : undefined
            }
          >
            {failure}
          </Banner>
        </div>
      )}

      {notices.length > 0 && (
        <div className={styles.stack}>
          {notices.map((notice, index) => (
            <Banner
              key={`${notice.code}-${index}`}
              tone={notice.level}
              urgent={notice.level === 'error'}
              actions={
                onDismiss !== undefined && index === 0 && !hasFailure ? (
                  <Button variant="secondary" onClick={onDismiss}>
                    Dismiss
                  </Button>
                ) : undefined
              }
            >
              {notice.message}
            </Banner>
          ))}
        </div>
      )}
    </>
  )
}
